import {ActionList, Banner} from '@primer/react'

import {LABELS} from '../../../constants/labels'
import type {RelatesToIssueNode} from '../../../mocks/relates-to/relates-to-types'
import {DependencyIssue, type DependencyIssueFragment} from './DependencyIssue'

export type RelationshipsRelatesToListViewProps = {
  relatesToNodes: readonly RelatesToIssueNode[]
  onLinkClick?: (event: MouseEvent) => void
}

// The single mock→row bridge: the mock node types `state`/`stateReason` as `string`, but the row expects the
// GraphQL enums. Centralized so both the sidebar group and this list view share one documented cast to delete
// at #23079, when the real fragment makes it unnecessary.
export function asDependencyIssueFragment(node: RelatesToIssueNode): DependencyIssueFragment {
  return node as DependencyIssueFragment
}

// Upper bound on rows rendered at once. "Relates to" is capped at 100 server-side (github/github#441190), but
// this mock view renders every row eagerly and each `DependencyIssue` preheats its full issue-viewer payload on
// mount, so we cap low to bound that prefetch burst. Matches blocked-by/blocking's `PAGE_SIZE` (25). This whole
// eager path is temporary — TODO(#23079): replace with the paginated Relay list view (no per-row preheat).
const MAX_VIEW_ALL_ROWS = 25

// Mock-backed "view all" body: the store holds every relation in memory, so this renders the (bounded) full
// set with no Relay pagination and the simpler `DependencyIssue` row rather than blockedBy/blocking's richer
// `ListView` item. Both are intentional Tier-1 tradeoffs. TODO(#23079): replace with the paginated Relay list
// view (rich rows + `Load more`) once the `relatedIssues` connection lands.
export function RelationshipsRelatesToListView({relatesToNodes, onLinkClick}: RelationshipsRelatesToListViewProps) {
  // Defensive: the caller only renders this modal when there are >3 relations, so this branch is unreachable today.
  if (relatesToNodes.length === 0) {
    return (
      <Banner variant="info">
        <Banner.Title>{LABELS.emptyRelatesToList}</Banner.Title>
      </Banner>
    )
  }

  return (
    <ActionList variant="full">
      {relatesToNodes.slice(0, MAX_VIEW_ALL_ROWS).map(node => (
        <DependencyIssue key={node.id} issue={asDependencyIssueFragment(node)} onLinkClick={onLinkClick} />
      ))}
    </ActionList>
  )
}
